import { AbstractClient, CompositionClient } from "@/app/client";
import { UUID } from "crypto";
import type {
  FeatureFlagDefinition,
  FeatureFlagsResponse,
  UpdateFeatureFlagsRequest,
} from "../types/featureFlags";

export default class APIFeatureFlags extends CompositionClient {
  constructor(client: AbstractClient) {
    super(client);
  }

  /**
   * Get current user's combined feature flags (from JWT token)
   * Fast - no database query required
   */
  getMyFeatureFlags = async (): Promise<FeatureFlagsResponse> => {
    const res = await this._fetch({
      url: `/feature-flags/me`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json") {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  /**
   * Get company-level feature flags
   */
  getCompanyFeatureFlags = async (): Promise<FeatureFlagsResponse> => {
    const res = await this._fetch({
      url: `/feature-flags/company`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json") {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  /**
   * Get user-specific feature flags (overrides only)
   */
  getUserFeatureFlags = async (): Promise<FeatureFlagsResponse> => {
    const res = await this._fetch({
      url: `/feature-flags/user`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json") {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  /**
   * Full feature-flag registry: every flag's name, type, default,
   * description, allowed values, scope, and deprecation status.
   * Static per backend deploy — cache for the session.
   */
  getRegistry = async (): Promise<FeatureFlagDefinition[]> => {
    const res = await this._fetch({
      url: `/feature-flags/registry`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json") {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  /**
   * Update company feature flags (admin only)
   */
  updateCompanyFeatureFlags = async (
    request: UpdateFeatureFlagsRequest,
  ): Promise<FeatureFlagsResponse> => {
    const res = await this._fetch({
      url: `/feature-flags/company`,
      method: "PUT",
      body: request,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json") {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  /**
   * Update user feature flags
   */
  updateUserFeatureFlags = async (
    userId: UUID,
    request: UpdateFeatureFlagsRequest,
  ): Promise<FeatureFlagsResponse> => {
    const res = await this._fetch({
      url: `/feature-flags/user/${userId}`,
      method: "PUT",
      body: request,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json") {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  /**
   * Reset company feature flags to defaults (admin only)
   */
  resetCompanyFeatureFlags = async (): Promise<FeatureFlagsResponse> => {
    const res = await this._fetch({
      url: `/feature-flags/company/reset`,
      method: "POST",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json") {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  /**
   * Reset user feature flags (will inherit from company)
   */
  resetUserFeatureFlags = async (
    userId: UUID,
  ): Promise<FeatureFlagsResponse> => {
    const res = await this._fetch({
      url: `/feature-flags/user/${userId}/reset`,
      method: "POST",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json") {
      return res.json();
    }
    throw new Error("Bad content type");
  };
}
